// Support (complaints and feedback) helpers: loading the backend lists and
// mapping their rows onto the design's vocabulary (state/support.tsx).
// Used by the Support dashboard, Complaints, ComplaintDetail, Feedback and
// ComplaintFormDrawer.
import { apiGet } from "../crm/contactsKit";

/* ---------- Loading ---------- */

/**
 * Every page of GET /api/complaints or /api/feedbacks (they answer
 * `{ data: { <key>: [...], pagination: { total_pages } } }`), limit 100 a page.
 * Resolves to null when the first page fails.
 */
export async function loadAll(path, key, params = {}, maxPages = 30) {
  const out = [];
  for (let page = 1; page <= maxPages; page += 1) {
    const r = await apiGet(path, { ...params, page, limit: 100 });
    if (!r?.success) return page === 1 ? null : out;
    out.push(...(r.data?.[key] || []));
    if (page >= (r.data?.pagination?.total_pages || 1)) break;
  }
  return out;
}

/** The selected location as query params (none for "All locations"). */
export const facilityParams = (fac) => {
  const id = fac.apiParams().facility_id;
  return id ? { facility_id: id } : {};
};

/** "All locations" or the selected facility's name (design: facilityScope.label). */
export const scopeLabel = (fac) => (fac.currentFacility ? fac.currentFacility.name : "All locations");

const toMs = (v) => (v ? new Date(v).getTime() : null);
const clean = (s) => String(s || "").trim();

/* ---------- Complaints ---------- */

/** complaints.status → the design's status words (migration 185 added acknowledged, waiting_on_patient and reopened; item 207). */
export const COMPLAINT_STATUS_LABEL = { open: "Open", acknowledged: "Acknowledged", in_progress: "In progress", waiting_on_patient: "Waiting on patient", reopened: "Reopened", resolved: "Resolved", closed: "Closed" };
export const COMPLAINT_STATUS_OPTIONS = Object.entries(COMPLAINT_STATUS_LABEL).map(([value, label]) => ({ value, label }));
export const COMPLAINT_STATUSES = Object.values(COMPLAINT_STATUS_LABEL);
export const complaintStatusKey = (label) => Object.keys(COMPLAINT_STATUS_LABEL).find((k) => COMPLAINT_STATUS_LABEL[k] === label) || "";
/** Statuses that still need work (design: openStatuses): everything but resolved and closed, as the backend's SLA clock counts. */
export const OPEN_COMPLAINT = ["open", "acknowledged", "in_progress", "waiting_on_patient", "reopened"];
export const DONE_COMPLAINT = ["resolved", "closed"];

/** complaints.channel → the design's channel words (item 203). */
export const CHANNEL_LABEL = { whatsapp: "WhatsApp", phone: "Phone call", in_clinic: "In clinic", email: "Email", google_review: "Google review", ai_front_desk: "AI front desk" };
export const CHANNEL_OPTIONS = Object.entries(CHANNEL_LABEL).map(([value, label]) => ({ value, label }));
export const CHANNELS = Object.values(CHANNEL_LABEL);

/** Hours as "45 min", "6 h", "2 d 4 h" (first response, SLA). */
export function hoursText(hours) {
  if (hours === null || hours === undefined || !Number.isFinite(Number(hours))) return "";
  const h = Math.abs(Number(hours));
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} min`;
  if (h < 24) return `${Math.round(h * 10) / 10} h`.replace(".0 h", " h");
  const d = Math.floor(h / 24);
  const rest = Math.round(h - d * 24);
  return rest ? `${d} d ${rest} h` : `${d} d`;
}

/**
 * The SLA badge for a complaint (item 202): due time from sla_due_at, and the
 * backend's overdue flag (open and past due). Done complaints say whether they
 * were resolved in time.
 */
export function slaState(c, now = Date.now()) {
  if (!c.slaDueAt) return null;
  if (DONE_COMPLAINT.includes(c.status)) {
    const at = c.resolvedAt || c.closedAt;
    if (!at) return { tone: "neutral", text: "Closed" };
    return at <= c.slaDueAt ? { tone: "success", text: "Met SLA" } : { tone: "danger", text: `Missed by ${hoursText((at - c.slaDueAt) / 3_600_000)}` };
  }
  const left = (c.slaDueAt - now) / 3_600_000;
  if (c.overdue || left < 0) return { tone: "danger", text: `Overdue ${hoursText(-left)}` };
  return { tone: left <= 4 ? "warning" : "neutral", text: `Due in ${hoursText(left)}` };
}

/** complaints.priority → the design's priority words ("urgent" is the design's "Critical"). */
export const PRIORITY_LABEL = { low: "Low", medium: "Medium", high: "High", urgent: "Critical" };
export const SEVERITIES = ["Low", "Medium", "High", "Critical"];
export const severityKey = (label) => Object.keys(PRIORITY_LABEL).find((k) => PRIORITY_LABEL[k] === label) || "medium";

/** GET /api/complaints (or /:id) row → the design's Complaint. */
export function toComplaint(row) {
  const name = clean(row.contact_name) || clean(row.patient_name);
  return {
    id: row.id,
    code: row.ticket_number || `#${row.id}`,
    contactId: row.contact_id || null,
    patientId: row.patient_id || null,
    facilityId: row.facility_id || null,
    location: row.facility_name || "",
    name,
    phone: row.contact_phone || row.patient_phone || "",
    email: row.contact_email || row.patient_email || "",
    subject: row.subject || "",
    description: row.description || "",
    categoryId: row.category_id || null,
    category: row.category_name || "",
    priority: row.priority || "medium",
    severity: PRIORITY_LABEL[row.priority] || "Medium",
    status: row.status || "open",
    statusLabel: COMPLAINT_STATUS_LABEL[row.status] || "Open",
    assigneeId: row.assigned_to || null,
    assignee: row.assigned_to_name || "",
    createdAt: toMs(row.created_at) || 0,
    resolvedAt: toMs(row.resolved_at),
    resolution: row.resolution_notes || "",
    replyCount: Number(row.reply_count) || 0,
    replies: row.replies || [],
    // Items 202–209
    slaDueAt: toMs(row.sla_due_at),
    overdue: !!row.overdue,
    closedAt: toMs(row.closed_at),
    channel: row.channel || "",
    channelLabel: CHANNEL_LABEL[row.channel] || "",
    doctorId: row.doctor_id || null,
    doctor: row.doctor_name || "",
    appointmentId: row.appointment_id || null,
    appointmentDate: row.appointment_date || "",
    appointmentTime: row.appointment_time || "",
    compensation: row.compensation_note || "",
    satisfactionRequestedAt: toMs(row.satisfaction_requested_at),
    satisfactionScore: row.satisfaction_score === null || row.satisfaction_score === undefined ? null : Number(row.satisfaction_score),
    firstResponseAt: toMs(row.first_response_at),
  };
}

/* ---------- Feedback: see feedbackKit.js ---------- */

export * from "./feedbackKit";

/* ---------- Small parts (design: screens/support/*.tsx) ---------- */

export function SeverityWord({ value }) {
  const cls = value === "Critical" ? "bg-danger-soft text-danger" : value === "High" ? "bg-warning-soft text-warning" : value === "Medium" ? "bg-sky-soft text-ink" : "bg-mist text-muted";
  return <span className={`inline-flex h-7 items-center rounded-full px-2.5 text-caption font-semibold ${cls}`}>{value}</span>;
}
